import { Button } from 'components';
import { LinearProgress } from 'components/LinearProgress';
import { Format } from 'helpers/Format';
import { FC, useEffect, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router';
import { IAdvertisementList } from 'types/Advertisement';

import LeftIcon from '@mui/icons-material/ChevronLeft';
import RightIcon from '@mui/icons-material/ChevronRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Box,
  Collapse,
  Fab,
  IconButton,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

interface IAdvertisementListProps extends IAdvertisementList {
  currentPage: number;
  setPage: Function;
  maxPage: number;
  loading: boolean;
}

interface IAdvertisementRow {
  advertisement: any;
}

const AdvertisementRow: FC<IAdvertisementRow> = ({ advertisement }) => {
  const [open, setOpen] = useState(false);
  const history = useHistory();
  const { path } = useRouteMatch();

  const handleEdit = () => {
    history.push(`${path}/edit`, { advertisement });
  };

  return (
    <>
      <TableRow
        key={advertisement._id}
        sx={{
          '&:hover, &:hover + *': { backgroundColor: '#ddd' },
        }}
      >
        <TableCell component="th" scope="row">
          {advertisement?.partner?.socialReason}
        </TableCell>
        <TableCell>{advertisement.title}</TableCell>
        <TableCell>{Format.birthdate(advertisement.createdAt)}</TableCell>
        <TableCell sx={{ wordBreak: 'break-all' }}>
          <Link href={`${advertisement.url}`} target="_blank">
            {advertisement.url}
          </Link>
        </TableCell>
        <TableCell>{advertisement.active ? 'Ativado' : 'Desativado'}</TableCell>
        <TableCell align="right">
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell
          style={{ paddingBottom: 0, paddingTop: 0, border: 0 }}
          colSpan={6}
        >
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Table size="small" aria-label="more-info">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Typography>
                      <strong>Criado em: </strong>
                      {Format.birthdate(
                        advertisement.partner?.partnershipContract?.createdAt
                      )}
                    </Typography>
                    <Typography>
                      <strong>Contrato: </strong>
                      <Link
                        href={`${advertisement.partner?.partnershipContract?.url}`}
                        target="_blank"
                      >
                        {advertisement.partner?.partnershipContract?.name}
                      </Link>
                    </Typography>
                    <Typography>
                      <strong>Banner: </strong>
                      <Link href={`${advertisement?.image?.url}`}>
                        {advertisement?.image?.name}
                      </Link>
                    </Typography>
                    <Box py={1}>
                      <Button onClick={handleEdit}>Editar</Button>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>
            </Table>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export const AdvertisementList: FC<IAdvertisementListProps> = ({
  list,
  currentPage,
  setPage,
  maxPage,
  loading,
}) => {
  const previousPage = () => {
    if (currentPage > 1) {
      setPage(currentPage - 1);
    }
  };

  const nextPage = () => {
    if (currentPage < maxPage) {
      setPage(currentPage + 1);
    }
  };

  useEffect(() => {
    setPage(1);
  }, [maxPage, setPage]);

  return (
    <TableContainer component={Paper} sx={{ mb: 5 }}>
      {loading && <LinearProgress />}
      <Table size="small" aria-label="a dense table">
        <TableHead>
          <TableRow>
            <TableCell>
              <strong>Instituição parceira</strong>
            </TableCell>
            <TableCell>
              <strong>Título do anúncio</strong>
            </TableCell>
            <TableCell>
              <strong>Publicado</strong>
            </TableCell>
            <TableCell>
              <strong>Link</strong>
            </TableCell>
            <TableCell>
              <strong>Status</strong>
            </TableCell>
            <TableCell align="right">
              <strong>Ver mais</strong>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {list?.map((advertisement, index) => (
            <AdvertisementRow key={index} advertisement={advertisement} />
          ))}
          <TableRow>
            <TableCell align="right" colSpan={6}>
              <Typography>
                <Fab
                  color="primary"
                  size="small"
                  aria-label="remove"
                  disabled={currentPage <= 1}
                  onClick={previousPage}
                >
                  <LeftIcon />
                </Fab>
                <Fab
                  disabled
                  size="small"
                  aria-label="add"
                  sx={{ ml: 1, mr: 1 }}
                >
                  {currentPage}
                </Fab>
                <Fab
                  color="primary"
                  size="small"
                  aria-label="add"
                  disabled={currentPage === maxPage}
                  onClick={nextPage}
                >
                  <RightIcon />
                </Fab>
              </Typography>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

